/**
 * <seren-accordion> — the collapsing rows used by the Seren v7 page set.
 *
 * Markup contract:
 *   <seren-accordion single>
 *     <div data-sa-item>
 *       <button data-sa-toggle aria-expanded="false" aria-controls="ID">
 *         … <span data-sa-sign></span>
 *       </button>
 *       <div class="…__panel" id="ID" data-sa-panel>   <- grid-template-rows: 0fr
 *         <div class="…__panel-clip">                  <- overflow: hidden, NO padding
 *           <div class="…__panel-body"> … </div>       <- padding lives here
 *         </div>
 *       </div>
 *     </div>
 *   </seren-accordion>
 *
 * The clip element must carry no padding of its own: padding counts toward a
 * grid item's minimum contribution, so a 0fr track would floor at the padding
 * height instead of collapsing to nothing.
 *
 * Attributes:
 *   single  — only one row open at a time
 */
class SerenAccordion extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const toggle = event.target.closest('[data-sa-toggle]');
    if (!toggle || !this.contains(toggle)) return;

    const isOpen = toggle.getAttribute('aria-expanded') === 'true';

    if (this.hasAttribute('single') && !isOpen) {
      this.querySelectorAll('[data-sa-toggle][aria-expanded="true"]').forEach((other) => {
        if (other !== toggle) this.setOpen(other, false);
      });
    }

    this.setOpen(toggle, !isOpen);
  };

  setOpen(toggle, open) {
    toggle.setAttribute('aria-expanded', String(open));

    const item = toggle.closest('[data-sa-item]') || this;
    const panel = item.querySelector('[data-sa-panel]');
    if (panel) panel.dataset.saOpen = open ? 'true' : 'false';

    // The sign is drawn in CSS off aria-expanded. A text glyph sits on the font's
    // math axis rather than the middle of the em box, so a minus rode high in its circle.
  }
}

if (!customElements.get('seren-accordion')) {
  customElements.define('seren-accordion', SerenAccordion);
}
